/**
 * TaskRequestTypeDetailsEditable
 *
 * Read + inline-edit for a task's per-request-type details. Editable by ANY user (mirrors the
 * full-page form, unlike comments which are author-only).
 *
 * Registry-driven and type-agnostic:
 * - READ: the type's details card (RequestTypeDetailsView dispatches custom/generic) + any
 *   task attachments.
 * - EDIT: the type's custom `EditFields` (e.g. IO) if it has one, otherwise the generic
 *   `DynamicRequestEditFields` rendered from its declarative `fields`. File/image attachments
 *   can be added; they're uploaded on Save and connected to the task. Saves the serialized blob
 *   to Task.requestTypeDetails via `def.serialize`.
 *
 * Adding a new request type needs NO change here — a `fields` entry is edited generically and a
 * complex type plugs in a custom `EditFields`.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, message, Image } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import RequestTypeDetailsView from './RequestTypeDetailsView';
import { getRequestTypeDef, DynamicRequestEditFields } from '../forms/requestTypes';
import { handleUploadFilesToS3, getAddedMentionedUserIds } from '../../utils/task.utils';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import TaskInlineAttachments from '../TaskInlineAttachments';
import S3File from '../../../../shared/S3File';
import { S3Image } from '../../../CampaignLauncher/components';

const parseDetails = (raw) => {
  let d = raw;
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d);
    } catch (e) {
      d = {};
    }
  }
  return d || {};
};

const TaskRequestTypeDetailsEditable = ({ task, updateTask, refetchTask }) => {
  const { mentionUsers } = useTaskContext();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [images, setImages] = useState([]);

  const def = task ? getRequestTypeDef(task.requestType) : null;
  if (!task || !def) return null;

  const EditFields = def.EditFields;
  // Editable if the type has custom edit fields (e.g. IO) or declarative `fields` (generic edit).
  const canEdit = !!(EditFields || def.fields);

  // Values the saved task actually carries — re-derived on every edit-open (see openEdit).
  const populated = () => def.populate(parseDetails(task.requestTypeDetails), task);

  const openEdit = () => {
    // `form` outlives each edit session and rc-field-form merges `initialValues` UNDER the
    // surviving store, so a previous session's values reappear on re-open. When a save dropped
    // a value, the re-opened editor still showed it and it looked saved (issue #11760). Seed
    // the store from the task every time — `populate` returns every key (undefined when the
    // task has no value), so stale ones are cleared rather than left behind.
    form.setFieldsValue(populated());
    setIsEditing(true);
  };

  const close = () => {
    setIsEditing(false);
    setFiles([]);
    setImages([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // getFieldsValue(true) so unrendered values (e.g. the watched dealType, money fields hidden
      // by the current deal type) are serialized too.
      const values = form.getFieldsValue(true);
      const data = { requestTypeDetails: def.serialize(values) };
      // Mirror the create/edit form: let the type apply its task side-effects (e.g. IO copies the
      // flight-start date to task.startDate) so the inline editor stays consistent with the form.
      if (def.applyToTask) def.applyToTask(values, data);
      const uploadedFiles = files.length ? await handleUploadFilesToS3(files) : [];
      const uploadedImages = images.length ? await handleUploadFilesToS3(images) : [];
      if (uploadedFiles.length) data.attachedFiles = { create: uploadedFiles };
      if (uploadedImages.length) data.attachedImages = { create: uploadedImages };
      // Ping only newly-added @mentions in the type's free-text field (e.g. the IO Note).
      const mentions = def.mentionField
        ? getAddedMentionedUserIds(
            parseDetails(task.requestTypeDetails)[def.mentionField],
            values[def.mentionField],
            mentionUsers
          )
        : [];
      await updateTask({ variables: { where: { id: task.id }, data, mentions } });
      if (refetchTask) refetchTask();
      message.success('Details updated.');
      close();
    } catch (e) {
      message.error('Failed to update details.');
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Form form={form} initialValues={populated()} className="task-rtd task-rtd--editing">
        <Text className="task-rtd__title">{def.detailsTitle || 'REQUEST DETAILS'}</Text>
        {EditFields ? <EditFields form={form} /> : <DynamicRequestEditFields fields={def.fields} />}
        <TaskInlineAttachments
          files={files}
          setFiles={setFiles}
          images={images}
          setImages={setImages}
        />
        <div className="task-rtd-editable__actions">
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save
          </Button>
        </div>
      </Form>
    );
  }

  const attachedFiles = task.attachedFiles || [];
  const attachedImages = task.attachedImages || [];

  return (
    <div className="task-rtd-editable">
      {updateTask && canEdit && (
        <Button
          type="text"
          icon={<EditOutlined />}
          className="task-rtd-editable__edit"
          onClick={openEdit}
          aria-label="Edit details"
        />
      )}
      <RequestTypeDetailsView task={task} />
      {(attachedFiles.length > 0 || attachedImages.length > 0) && (
        <div className="task-rtd__attachments">
          {attachedFiles.map((file, i) => (
            <S3File key={`file-${i}`} file={file} />
          ))}
          {attachedImages.length > 0 && (
            <Image.PreviewGroup>
              {attachedImages.map((file, i) => (
                <S3Image
                  key={`img-${i}`}
                  imgKey={file.key}
                  height="25%"
                  width="25%"
                  style={{ maxWidth: '100px' }}
                />
              ))}
            </Image.PreviewGroup>
          )}
        </div>
      )}
    </div>
  );
};

TaskRequestTypeDetailsEditable.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string,
    requestType: PropTypes.string,
    requestTypeDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    attachedFiles: PropTypes.array,
    attachedImages: PropTypes.array,
  }),
  updateTask: PropTypes.func,
  refetchTask: PropTypes.func,
};

TaskRequestTypeDetailsEditable.defaultProps = {
  task: null,
  updateTask: undefined,
  refetchTask: undefined,
};

export default TaskRequestTypeDetailsEditable;
